/* =========================================================================
   LaneCult — Product page behaviour
   Three small custom elements. All of them re-initialise themselves when Dawn
   re-renders markup (variant changes, product swaps), so no global state.
   Depends only on Dawn globals that already ship with the theme
   (subscribe / PUB_SUB_EVENTS from pubsub.js + constants.js).
   ========================================================================= */
(() => {
  const define = (name, ctor) => {
    if (!customElements.get(name)) customElements.define(name, ctor);
  };

  // Run fn at most once per animation frame.
  const throttleFrame = (fn) => {
    let queued = false;
    return (...args) => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        fn(...args);
      });
    };
  };

  /* ── <lc-gallery-dots> ────────────────────────────────────────────────
     Moves itself into Dawn's mobile gallery slider and mirrors its position. */
  class LcGalleryDots extends HTMLElement {
    connectedCallback() {
      const viewer = document.getElementById(`GalleryViewer-${this.dataset.section}`);
      if (!viewer) return;
      if (this.parentElement !== viewer) {
        viewer.appendChild(this); // re-runs connectedCallback inside the viewer
        return;
      }

      this.slider = viewer.querySelector('[id^="Slider-Gallery"]') || viewer.querySelector('.slider');
      if (!this.slider) return;

      this.onScroll = throttleFrame(() => this.update());
      this.rebuild = throttleFrame(() => this.build());

      this.slider.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.rebuild);
      this.observer = new MutationObserver(this.rebuild);
      this.observer.observe(this.slider, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class'],
      });

      this.build();
    }

    disconnectedCallback() {
      this.slider?.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.rebuild);
      this.observer?.disconnect();
    }

    slides() {
      return Array.from(this.slider.children).filter((el) => el.offsetWidth > 0);
    }

    build() {
      const slides = this.slides();
      this.hidden = slides.length < 2;

      if (this.children.length !== slides.length) {
        const dots = slides.map((_, index) => {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'lc-gallery-dots__dot';
          dot.setAttribute('aria-label', `${index + 1} / ${slides.length}`);
          dot.addEventListener('click', () => this.goTo(index));
          return dot;
        });
        this.replaceChildren(...dots);
      }
      this.update();
    }

    goTo(index) {
      const slides = this.slides();
      if (!slides[index]) return;
      this.slider.scrollTo({ left: slides[index].offsetLeft - slides[0].offsetLeft, behavior: 'smooth' });
    }

    update() {
      const slides = this.slides();
      if (!slides.length) return;
      const base = slides[0].offsetLeft;
      const x = this.slider.scrollLeft;
      let active = 0;
      let best = Infinity;
      slides.forEach((slide, index) => {
        const distance = Math.abs(slide.offsetLeft - base - x);
        if (distance < best) {
          best = distance;
          active = index;
        }
      });
      Array.from(this.children).forEach((dot, index) => {
        const current = index === active ? 'true' : 'false';
        if (dot.getAttribute('aria-current') !== current) dot.setAttribute('aria-current', current);
      });
    }
  }

  /* ── <lc-option-row> ─────────────────────────────────────────────────
     One-line option row: keeps the selected value in view and lets the
     chevron scroll the row when it overflows. */
  class LcOptionRow extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.lc-opt-track');
      this.more = this.querySelector('.lc-opt-more');
      if (!this.track) return;

      this.measure = throttleFrame(() => this.updateState());
      this.track.addEventListener('scroll', this.measure, { passive: true });
      this.resizeObserver = new ResizeObserver(this.measure);
      this.resizeObserver.observe(this.track);

      this.onMore = () => {
        this.track.scrollBy({ left: Math.max(this.track.clientWidth * 0.75, 48), behavior: 'smooth' });
      };
      this.more?.addEventListener('click', this.onMore);

      requestAnimationFrame(() => {
        this.revealSelected();
        this.updateState();
      });
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
      this.track?.removeEventListener('scroll', this.measure);
      this.more?.removeEventListener('click', this.onMore);
    }

    revealSelected() {
      const label = this.track.querySelector('input:checked + label');
      if (!label) return;
      const trackBox = this.track.getBoundingClientRect();
      const labelBox = label.getBoundingClientRect();
      if (labelBox.right > trackBox.right) this.track.scrollLeft += labelBox.right - trackBox.right + 16;
      else if (labelBox.left < trackBox.left) this.track.scrollLeft -= trackBox.left - labelBox.left + 16;
    }

    updateState() {
      const t = this.track;
      const overflow = t.scrollWidth > t.clientWidth + 1;
      const atEnd = t.scrollLeft + t.clientWidth >= t.scrollWidth - 1;
      this.toggleAttribute('data-overflow', overflow);
      this.toggleAttribute('data-at-end', overflow && atEnd);
    }
  }

  /* ── <lc-sticky-atc> ─────────────────────────────────────────────────
     Mirrors Dawn's real add-to-cart button and forwards clicks to it, so
     variant, quantity, selling plan, cart drawer and errors stay Dawn's. */
  class LcStickyAtc extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.section;
      this.mode = this.dataset.mode || 'always';
      this.button = this.querySelector('.lc-sticky__btn');
      this.label = this.querySelector('.lc-sticky__label');
      this.root = this.closest('product-info') || document.body;
      this.mediaQuery = window.matchMedia('(max-width: 749px)');
      this.pastMain = false;

      this.onClick = this.onClick.bind(this);
      this.updateVisibility = this.updateVisibility.bind(this);
      this.sync = throttleFrame(() => this.syncButton());

      this.button?.addEventListener('click', this.onClick);
      this.mediaQuery.addEventListener?.('change', this.updateVisibility);

      // Mirror disabled / sold-out / loading state of the main button.
      this.mutationObserver = new MutationObserver((records) => {
        if (records.some((record) => !this.contains(record.target))) this.sync();
      });
      this.mutationObserver.observe(this.root, {
        subtree: true,
        childList: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['disabled', 'aria-disabled', 'class'],
      });

      // Keep page content clear of the bar.
      this.resizeObserver = new ResizeObserver(() => {
        document.documentElement.style.setProperty('--lc-sticky-h', `${this.offsetHeight}px`);
      });
      this.resizeObserver.observe(this);

      // Refresh title / price / image / entries from Dawn's re-rendered section HTML.
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => this.onVariantChange(event));
      }

      if (this.mode === 'scroll') this.observeMainButton();
      this.syncButton();
      this.updateVisibility();
    }

    disconnectedCallback() {
      this.button?.removeEventListener('click', this.onClick);
      this.mediaQuery?.removeEventListener?.('change', this.updateVisibility);
      this.mutationObserver?.disconnect();
      this.resizeObserver?.disconnect();
      this.intersectionObserver?.disconnect();
      this.unsubscribe?.();
      document.body.classList.remove('lc-sticky-on');
      document.documentElement.style.removeProperty('--lc-sticky-h');
    }

    mainButton() {
      return document.getElementById(`ProductSubmitButton-${this.sectionId}`);
    }

    observeMainButton() {
      const target = this.mainButton();
      if (!target) return;
      this.intersectionObserver = new IntersectionObserver(([entry]) => {
        this.pastMain = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        this.updateVisibility();
      });
      this.intersectionObserver.observe(target);
    }

    updateVisibility() {
      const show = this.mediaQuery.matches && (this.mode === 'always' || this.pastMain);
      this.classList.toggle('is-visible', show);
      this.toggleAttribute('inert', !show);
      const hidden = String(!show);
      if (this.getAttribute('aria-hidden') !== hidden) this.setAttribute('aria-hidden', hidden);
      document.body.classList.toggle('lc-sticky-on', show);
    }

    syncButton() {
      const main = this.mainButton();
      if (!main || !this.button) return;

      const disabled = main.hasAttribute('disabled');
      if (this.button.disabled !== disabled) this.button.disabled = disabled;

      const busy = main.getAttribute('aria-disabled') === 'true' || main.classList.contains('loading');
      this.button.classList.toggle('is-loading', busy);

      const text = (main.querySelector('span') || main).textContent.trim();
      if (text && this.label && this.label.textContent !== text) this.label.textContent = text;
    }

    onVariantChange(event) {
      const data = event && event.data;
      if (!data || data.sectionId !== this.sectionId || !data.html) return;
      const id = `LcStickyInfo-${this.sectionId}`;
      const fresh = data.html.getElementById ? data.html.getElementById(id) : data.html.querySelector(`#${id}`);
      const current = this.querySelector(`[id="${id}"]`);
      if (fresh && current) current.innerHTML = fresh.innerHTML;
      this.sync();
    }

    onClick() {
      const main = this.mainButton();
      if (!main || main.disabled || main.getAttribute('aria-disabled') === 'true') return;
      main.click();
    }
  }

  define('lc-gallery-dots', LcGalleryDots);
  define('lc-option-row', LcOptionRow);
  define('lc-sticky-atc', LcStickyAtc);
})();
